import { IDBPDatabase, openDB } from 'idb';

import { InjectDebugLogger } from '../../framework/logging/decorators/InjectDebugLogger';
import { LogFormat } from '../../framework/logging/decorators/LogFormat';
import { LogMethod } from '../../framework/logging/decorators/LogMethod';

import ObjectStorage from './ObjectStorage';

/**
 * Name of the object store inside the IndexedDB database.
 */
const STORE_NAME = 'objects';

const ERROR_NOT_OPEN = "Database isn't open";

/**
 * IndexedDB implementation of {@link ObjectStorage} API.
 *
 * Each open `IndexedDBObjectStorage` instance with distinct {@link dbName}
 * represents a distinct IndexedDB database with a single {@link STORE_NAME}
 * object store, as opposed to multiple object stores in a single IndexedDB
 * database.
 *
 * @param T - Stored objects type.
 */
@InjectDebugLogger('idb')
class IndexedDBObjectStorage<T> implements ObjectStorage<T> {
  private dbName: string | null = null;
  private db: Promise<IDBPDatabase> | null = null;

  @LogMethod
  public open(@LogFormat('%s') name: string): void {
    this.dbName = name;

    this.db = openDB(this.dbName, 1, {
      upgrade(database: IDBPDatabase): void {
        if (!database.objectStoreNames.contains(STORE_NAME)) {
          database.createObjectStore(STORE_NAME);
        }
      },
    });
  }

  @LogMethod
  public async get(@LogFormat('%s') name: string): Promise<T | undefined> {
    if (!this.db) {
      throw new Error(ERROR_NOT_OPEN);
    }

    const db = await this.db;
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    return store.get(name);
  }

  @LogMethod
  public async set(
    @LogFormat('%s') name: string,
    @LogFormat('%o') value: T
  ): Promise<void> {
    if (!this.db) {
      throw new Error(ERROR_NOT_OPEN);
    }

    const db = await this.db;
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    await store.put(value, name);

    return transaction.done;
  }

  @LogMethod
  public async delete(@LogFormat('%s') name: string): Promise<void> {
    if (!this.db) {
      throw new Error(ERROR_NOT_OPEN);
    }

    const db = await this.db;
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    await store.delete(name);

    return transaction.done;
  }

  @LogMethod
  public async deleteAll(): Promise<void> {
    if (!this.db) {
      throw new Error(ERROR_NOT_OPEN);
    }

    const db = await this.db;
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    await store.clear();

    return transaction.done;
  }
}

export default IndexedDBObjectStorage;
